import maplibregl from "maplibre-gl";
import { PMTiles, type RangeResponse, type Source } from "pmtiles";
import { readPackFile } from "../storage/opfs";
import { packFileName, type PackDescriptor } from "../storage/manifest";

/** Reads PMTiles byte ranges straight out of an OPFS file. */
class OpfsSource implements Source {
  constructor(
    private readonly file: File,
    private readonly key: string,
  ) {}

  getKey(): string {
    return this.key;
  }

  async getBytes(offset: number, length: number): Promise<RangeResponse> {
    return { data: await this.file.slice(offset, offset + length).arrayBuffer() };
  }
}

interface InstalledPack {
  descriptor: PackDescriptor;
  archive: PMTiles;
}

/**
 * Must be a new array every time. MapLibre posts tile buffers to its worker as
 * transferables, which detaches them; handing out one shared instance means the
 * second missing tile tries to transfer an already-detached buffer and the
 * whole source stalls.
 */
const emptyTile = () => new Uint8Array();

function tileIntersects(pack: PackDescriptor, z: number, x: number, y: number): boolean {
  const n = 2 ** z;
  const west = (x / n) * 360 - 180;
  const east = ((x + 1) / n) * 360 - 180;
  const lat = (row: number) =>
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * row) / n))) * 180) / Math.PI;
  const north = lat(y);
  const south = lat(y + 1);
  const [w, s, e, nBound] = pack.bounds;
  return !(e < west || w > east || nBound < south || s > north);
}

/**
 * A style source such as "s57" can be backed by several regional packs at once,
 * which the stock pmtiles Protocol cannot express (it maps one key to one
 * archive). This router registers a single `pmtiles://` handler that fans a
 * tile request out to whichever installed packs cover that tile.
 */
export class PackRouter {
  private readonly bySource = new Map<string, InstalledPack[]>();
  private readonly byPack = new Map<string, InstalledPack>();
  private registered = false;
  /** Request counters, surfaced for diagnostics. */
  readonly stats = { tileRequests: 0, tileHits: 0, jsonRequests: 0, errors: 0 };

  async load(descriptors: PackDescriptor[]): Promise<void> {
    this.bySource.clear();
    this.byPack.clear();
    for (const descriptor of descriptors) {
      const file = await readPackFile(packFileName(descriptor));
      if (!file) continue;
      const installed = {
        descriptor,
        archive: new PMTiles(new OpfsSource(file, descriptor.id)),
      };
      const list = this.bySource.get(descriptor.source) ?? [];
      list.push(installed);
      this.bySource.set(descriptor.source, list);
      this.byPack.set(descriptor.id, installed);
    }
  }

  packs(): PackDescriptor[] {
    return [...this.byPack.values()].map(({ descriptor }) => descriptor);
  }

  sources(): string[] {
    return [...this.bySource.keys()];
  }

  packCount(): number {
    let total = 0;
    for (const list of this.bySource.values()) total += list.length;
    return total;
  }

  private async tileJSON(packs: InstalledPack[], url: string) {
    let west = 180;
    let south = 90;
    let east = -180;
    let north = -90;
    let minzoom = 24;
    let maxzoom = 0;
    for (const { descriptor } of packs) {
      west = Math.min(west, descriptor.bounds[0]);
      south = Math.min(south, descriptor.bounds[1]);
      east = Math.max(east, descriptor.bounds[2]);
      north = Math.max(north, descriptor.bounds[3]);
      minzoom = Math.min(minzoom, descriptor.minzoom);
      maxzoom = Math.max(maxzoom, descriptor.maxzoom);
    }
    if (!packs.length) {
      // A source with nothing installed still needs valid TileJSON or the whole
      // style fails to load; its tiles simply resolve empty.
      return {
        tilejson: "3.0.0",
        tiles: [`${url}/{z}/{x}/{y}`],
        bounds: [-180, -85, 180, 85],
        minzoom: 0,
        maxzoom: 14,
      };
    }
    return {
      tilejson: "3.0.0",
      scheme: "xyz",
      tiles: [`${url}/{z}/{x}/{y}`],
      bounds: [west, south, east, north],
      minzoom,
      maxzoom,
    };
  }

  private packFromUrl(url: string): { packId: string; z?: number; x?: number; y?: number } | null {
    const match = /^pmtiles:\/\/pack\/([^/]+)(?:\/(\d+)\/(\d+)\/(\d+))?/.exec(url);
    if (!match) return null;
    try {
      return {
        packId: decodeURIComponent(match[1]),
        z: match[2] === undefined ? undefined : Number(match[2]),
        x: match[3] === undefined ? undefined : Number(match[3]),
        y: match[4] === undefined ? undefined : Number(match[4]),
      };
    } catch {
      return null;
    }
  }

  private async getPackTile(
    pack: InstalledPack,
    z: number,
    x: number,
    y: number,
    signal: AbortSignal,
  ): Promise<Uint8Array> {
    if (!tileIntersects(pack.descriptor, z, x, y)) return emptyTile();
    const result = await pack.archive.getZxy(z, x, y, signal);
    return result ? new Uint8Array(result.data) : emptyTile();
  }

  private async getTile(
    sourceId: string,
    z: number,
    x: number,
    y: number,
    signal: AbortSignal,
  ): Promise<Uint8Array> {
    const packs = this.bySource.get(sourceId);
    if (!packs) return emptyTile();
    for (const { descriptor, archive } of packs) {
      if (!tileIntersects(descriptor, z, x, y)) continue;
      const result = await archive.getZxy(z, x, y, signal);
      if (result) return new Uint8Array(result.data);
    }
    return emptyTile();
  }

  /** Installs the protocol handler. Must run before any map is constructed. */
  register(): void {
    if (this.registered) return;
    this.registered = true;
    maplibregl.addProtocol("pmtiles", async (params, abortController) => {
      const url = params.url;
      try {
        const packRoute = this.packFromUrl(url);
        if (packRoute) {
          const pack = this.byPack.get(packRoute.packId);
          if (!pack) throw new Error(`Unknown installed map: ${packRoute.packId}`);
          if (packRoute.z === undefined) {
            this.stats.jsonRequests++;
            return { data: await this.tileJSON([pack], `pmtiles://pack/${encodeURIComponent(packRoute.packId)}`) };
          }
          this.stats.tileRequests++;
          const data = await this.getPackTile(
            pack,
            packRoute.z,
            packRoute.x!,
            packRoute.y!,
            abortController.signal,
          );
          if (data.byteLength) this.stats.tileHits++;
          return { data };
        }

        // Keep the original source route for old cached styles and diagnostics.
        const match = /^pmtiles:\/\/([^/]+)(?:\/(\d+)\/(\d+)\/(\d+))?/.exec(url);
        if (!match) throw new Error(`Unsupported pmtiles URL: ${url}`);
        if (!match[2]) {
          this.stats.jsonRequests++;
          return {
            data: await this.tileJSON(this.bySource.get(match[1]) ?? [], `pmtiles://${match[1]}`),
          };
        }
        this.stats.tileRequests++;
        const data = await this.getTile(
          match[1],
          Number(match[2]),
          Number(match[3]),
          Number(match[4]),
          abortController.signal,
        );
        if (data.byteLength) this.stats.tileHits++;
        return { data };
      } catch (error) {
        this.stats.errors++;
        throw error;
      }
    });
  }

  unregister(): void {
    if (!this.registered) return;
    maplibregl.removeProtocol("pmtiles");
    this.registered = false;
  }
}
